import { useQuery } from "@tanstack/react-query";
import { MASTER_RV_TYPES } from "@/data/masterRvTypes";

const BASE_URL =
  import.meta.env['VITE_SUPABASE_URL'] +
  "/storage/v1/object/public/splash-assets/indexes/active_rv_types.json";

const CACHE_KEY = "active-rv-types:v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

type CountsMap = Record<string, number>;

function fallbackFromMaster(): CountsMap {
  const map: CountsMap = {};
  for (const t of MASTER_RV_TYPES) map[t] = 1;
  return map;
}

function readCache(): CountsMap | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; data: CountsMap };
    if (!parsed?.data || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: CountsMap) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore quota / privacy mode
  }
}

async function fetchOnce(timeoutMs: number): Promise<CountsMap | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    // Per-hour cache-buster — avoids stale Cloudflare MISS pinning, but stays cacheable within the hour.
    const bust = Math.floor(Date.now() / (60 * 60 * 1000));
    const res = await fetch(`${BASE_URL}?h=${bust}`, {
      signal: ctrl.signal,
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as CountsMap;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

async function fetchActiveTypes(): Promise<CountsMap> {
  const delays = [0, 300, 900, 2000];
  for (const d of delays) {
    if (d) await new Promise((r) => setTimeout(r, d));
    const result = await fetchOnce(5000);
    if (result) {
      writeCache(result);
      return result;
    }
  }
  // All attempts failed — use cache or master-list fallback.
  return readCache() ?? fallbackFromMaster();
}

/**
 * Returns a map of rv_type → count for types with active inventory.
 * Resilient to transient Supabase Storage 504s: 3 silent retries with
 * timeout, then falls back to localStorage cache, then to a map derived
 * from MASTER_RV_TYPES. Never throws — UI never sees an error.
 */
export function useActiveRvTypes() {
  return useQuery({
    queryKey: ["active-rv-types"],
    queryFn: fetchActiveTypes,
    initialData: () => readCache() ?? undefined,
    retry: 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
  });
}
